/**
 * The pandit roster and its skills — `/v1/admin/pandits*`.
 *
 * Two invariants live on the server and are deliberately not re-implemented here.
 *
 * **The status machine refuses illegal transitions**, and the activation gate refuses activation
 * until every requirement is met. The screen sends the action and shows what came back; it does not
 * decide which buttons are legal from the current status, because that table exists once already
 * and a second copy drifts.
 *
 * **Confirming a skill is what makes a pandit matchable for that puja.** `confirmed_at` is the only
 * thing the matching engine reads, so this is not a bookkeeping flag — it is the decision that a
 * stranger may be sent to somebody's home to perform that ritual. The mutations invalidate the
 * whole namespace because a confirmation also moves the detail screen's checklist.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type PanditRow = components['schemas']['AdminPanditRow'];
export type PanditDetail = components['schemas']['AdminPanditDetail'];
export type SkillRow = components['schemas']['AdminSkillRow'];
export type PanditQuality = components['schemas']['PanditQualityOut'];
type PanditList = components['schemas']['AdminPanditListResponse'];
type SkillList = components['schemas']['AdminSkillList'];
type StatusResponse = components['schemas']['StatusResponse'];
export type QualityActionIssue = components['schemas']['QualityActionIssueIn'];
type QualityActionOut = components['schemas']['QualityActionOut'];
export type PanditJob = components['schemas']['AdminPanditHistoryItem'];
type PanditJobs = components['schemas']['AdminPanditHistory'];
export type PanditAvailabilityView = components['schemas']['AdminPanditAvailability'];

export interface PanditQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  status?: string;
  city_id?: number;
  /** A name fragment, a `PDT…` code, or the phone's last four digits. */
  q?: string;
  is_listed?: boolean;
  /** Pandits holding this badge, active. */
  badge_type?: string;
}

/**
 * Exported for its own test. Every filter is omitted when absent rather than sent empty — the
 * server reads an empty `status` as a filter for the empty status and returns nothing.
 */
export function panditQueryString(params: PanditQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.status) search.set('status', params.status);
  if (params.city_id !== undefined) search.set('city_id', String(params.city_id));
  if (params.q) search.set('q', params.q);
  if (params.is_listed !== undefined) search.set('is_listed', String(params.is_listed));
  if (params.badge_type) search.set('badge_type', params.badge_type);
  return search.toString();
}

export function usePandits(params: PanditQuery) {
  return useQuery({
    queryKey: keys.pandits.list(params),
    queryFn: () => authedClient.request<PanditList>(`/v1/admin/pandits?${panditQueryString(params)}`),
  });
}

export function usePandit(id: number | undefined) {
  return useQuery({
    queryKey: keys.pandits.one(id ?? 0),
    queryFn: () => authedClient.request<PanditDetail>(`/v1/admin/pandits/${id}`),
    enabled: id !== undefined,
  });
}

export function usePanditSkills(id: number | undefined) {
  return useQuery({
    queryKey: keys.pandits.skills(id ?? 0),
    queryFn: () => authedClient.request<SkillList>(`/v1/admin/pandits/${id}/skills`),
    enabled: id !== undefined,
  });
}

/** Invalidates the whole namespace: a status change moves the list, the detail and the checklist. */
function usePanditMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.pandits.all() }),
  });
}

/** `reason_code` is what the pandit sees on a pause (suspend or deactivate only); `reason` stays
 * on the audit row for support, because it can say things a pandit should not read verbatim. */
export function useSetPanditStatus() {
  return usePanditMutation(
    ({ id, action, reason, reason_code }: { id: number; action: string; reason?: string; reason_code?: string }) =>
      authedClient.request<StatusResponse>(`/v1/admin/pandits/${id}/status`, {
        method: 'POST',
        body: { action, reason: reason ?? null, ...(reason_code ? { reason_code } : {}) },
      }),
  );
}

export function useSetPanditListing() {
  return usePanditMutation(({ id, is_listed, reason }: { id: number; is_listed: boolean; reason: string }) =>
    authedClient.request<StatusResponse>(`/v1/admin/pandits/${id}/listing`, {
      method: 'PATCH',
      body: { is_listed, reason },
    }),
  );
}

/**
 * The pandit's full phone number, once (admin doc 07 §3.2 item 1). A mutation, like the devotee
 * console's reveal: the server writes the audit row before it answers, and the number must never
 * sit in the query cache beside the detail that deliberately carries only the mask.
 */
export function useRevealPanditPhone() {
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      authedClient.request<components['schemas']['PhoneRevealResponse']>(`/v1/admin/pandits/${id}/reveal-phone`, {
        method: 'POST',
        body: { reason },
      }),
  });
}

/**
 * A formal quality consequence (docs/03-data/20 §7). The server decides what each type does to
 * matching; a `training_required` naming no categories holds the pandit back from every puja.
 */
export function useIssueQualityAction() {
  return usePanditMutation(({ id, body }: { id: number; body: QualityActionIssue }) =>
    authedClient.request<QualityActionOut>(`/v1/admin/pandits/${id}/quality-actions`, {
      method: 'POST',
      body,
    }),
  );
}

export function useRevokeQualityAction() {
  return usePanditMutation(({ actionId, reason }: { actionId: number; reason: string }) =>
    authedClient.request<QualityActionOut>(`/v1/admin/quality-actions/${actionId}/revoke`, {
      method: 'POST',
      body: { reason },
    }),
  );
}

export function useConfirmSkill() {
  return usePanditMutation(({ id, skillId }: { id: number; skillId: number }) =>
    authedClient.request<SkillRow>(`/v1/admin/pandits/${id}/skills/${skillId}/confirm`, {
      method: 'POST',
    }),
  );
}

export function useUnconfirmSkill() {
  return usePanditMutation(({ id, skillId, reason }: { id: number; skillId: number; reason: string }) =>
    authedClient.request<SkillRow>(`/v1/admin/pandits/${id}/skills/${skillId}/unconfirm`, {
      method: 'POST',
      body: { reason },
    }),
  );
}

export function useSetProficiency() {
  return usePanditMutation(
    ({ id, skillId, proficiency, reason }: { id: number; skillId: number; proficiency: string; reason: string }) =>
      authedClient.request<SkillRow>(`/v1/admin/pandits/${id}/skills/${skillId}/proficiency`, {
        method: 'POST',
        body: { proficiency, reason },
      }),
  );
}

export function useConfirmAllSkills() {
  return usePanditMutation(({ id, skillIds }: { id: number; skillIds: number[] }) =>
    authedClient.request<components['schemas']['BulkConfirmResponse']>(
      `/v1/admin/pandits/${id}/skills/confirm-all`,
      { method: 'POST', body: { skill_ids: skillIds } },
    ),
  );
}

/**
 * The quality read — score, trend, open flags and actions. Assembled by the server from rows the
 * nightly job wrote, so the console cannot disagree with the matcher about what a score is.
 */
export function usePanditQuality(id: number | undefined) {
  return useQuery({
    queryKey: keys.pandits.quality(id ?? 0),
    queryFn: () => authedClient.request<PanditQuality>(`/v1/admin/pandits/${id}/quality`),
    enabled: id !== undefined,
  });
}

/** The drawer's Jobs tab: recent assignments and processed payouts, newest first. */
export function usePanditJobs(id: number | undefined) {
  return useQuery({
    queryKey: keys.pandits.jobs(id ?? 0),
    queryFn: () => authedClient.request<PanditJobs>(`/v1/admin/pandits/${id}/jobs`),
    enabled: id !== undefined,
  });
}

/** The drawer's Availability tab: what the matcher reads about when this pandit can work. */
export function usePanditAvailability(id: number) {
  return useQuery({
    queryKey: keys.pandits.availability(id),
    queryFn: () => authedClient.request<PanditAvailabilityView>(`/v1/admin/pandits/${id}/availability`),
  });
}
